import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
	Box,
	Button,
	Grid,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
	DataGrid,
	GridCallbackDetails,
	GridCellModesModel,
	GridColDef,
	GridEventListener,
	GridPagination,
	GridPaginationModel,
	GridSortItem,
	GridSortModel,
} from '@mui/x-data-grid';
import * as React from 'react';
import { Link } from 'react-router-dom';

interface GridWithToolbarProps {
	title: string;
	data: any[];
	columns: GridColDef[];
	loading: boolean;
	isRefetching?: boolean;
	buttonText?: string;
	buttonLink?: string;
	buttonColor?: 'primary' | 'secondary';
	buttonIcon?: React.ReactNode;
	onButtonClick?: () => void;
	hideButton?: boolean;
	hideSearch?: boolean;
	rowId?: string;
	checkboxSelection?: boolean;
	autoHeight?: boolean;
	defaultSortItem?: GridSortItem;
	pageSize?: number;
	rowCount?: number;
	paginationMode?: 'server' | 'client';
	paginationModel?: GridPaginationModel;
	hasNextPage?: boolean;
	onPaginationModelChange?: (
		model: GridPaginationModel,
		details: GridCallbackDetails<any>,
	) => void;
	onEditRowsModelChange?: (model: GridCellModesModel) => void;
	onCellEditCommit?: GridEventListener<'cellEditStop'>;
}

interface TableHeaderProps {
	clearSearch: () => void;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	title: string;
	value: string;
	buttonText?: string;
	buttonLink?: string;
	buttonColor?: 'primary' | 'secondary';
	buttonIcon?: React.ReactNode;
	onButtonClick?: () => void;
	hideButton?: boolean;
	hideSearch?: boolean;
}

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: theme.palette.common.white,
	},
	actionButton: {
		minWidth: 0,
		height: theme.spacing(4),
		width: theme.spacing(4),
	},
}));

function escapeRegExp(value: string): string {
	return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function TableHeader({
	clearSearch,
	onChange,
	title,
	value,
	buttonText,
	buttonLink,
	buttonIcon,
	buttonColor = 'secondary',
	hideButton = false,
	hideSearch = false,
	onButtonClick = undefined,
}: TableHeaderProps) {
	return (
		<Grid
			container
			direction='row'
			justifyContent='space-between'
			alignItems='center'
			sx={{ pb: 2 }}
		>
			<Grid item>
				<Stack direction='row' spacing={2} alignItems='center'>
					<Typography variant='h3'>{title}</Typography>
					{!hideButton && buttonLink && (
						<Button
							color={buttonColor}
							variant='contained'
							component={Link}
							startIcon={buttonIcon}
							to={buttonLink}
						>
							{buttonText}
						</Button>
					)}
					{!hideButton && onButtonClick && (
						<Button
							color={buttonColor}
							variant='contained'
							startIcon={buttonIcon}
							onClick={onButtonClick}
						>
							{buttonText}
						</Button>
					)}
				</Stack>
			</Grid>
			<Grid item>
				{!hideSearch && (
					<TextField
						variant='outlined'
						sx={{ backgroundColor: 'white.lightest' }}
						size='small'
						value={value}
						onChange={onChange}
						placeholder='Search…'
						InputProps={{
							startAdornment: <SearchIcon fontSize='small' />,
							endAdornment: (
								<IconButton
									title='Clear'
									aria-label='Clear'
									size='small'
									sx={{ visibility: value ? 'visible' : 'hidden' }}
									onClick={clearSearch}
								>
									<ClearIcon fontSize='small' />
								</IconButton>
							),
						}}
					/>
				)}
			</Grid>
		</Grid>
	);
}

function CustomPagination(props: any) {
	return (
		<GridPagination
			nextIconButtonProps={{
				disabled: !props.hasNextPage,
			}}
		/>
	);
}

const GridWithToolbar = ({
	title,
	data,
	loading,
	columns,
	buttonText,
	buttonLink,
	buttonIcon = <AddIcon />,
	isRefetching,
	hideButton = false,
	hideSearch = false,
	buttonColor = 'secondary',
	onButtonClick = undefined,
	onEditRowsModelChange = null,
	onCellEditCommit = null,
	checkboxSelection = false,
	autoHeight = false,
	rowId = 'id',
	defaultSortItem = null,
	onPaginationModelChange = null,
	paginationMode = 'client',
	rowCount = null,
	paginationModel = null,
	hasNextPage = true,
	pageSize = 25,
}: GridWithToolbarProps) => {
	const classes = useStyles();
	const [searchText, setSearchText] = React.useState('');
	const [rows, setRows] = React.useState<any>();

	const requestSearch = React.useCallback(
		(searchValue: string) => {
			setSearchText(searchValue);
			const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
			const filteredRows = data
				? data.filter((row: any) => {
						return Object.keys(row).some((field: any) => {
							const value = row[field];
							if (value === null || value === undefined) {
								return false;
							}
							return searchRegex.test(row[field].toString());
						});
				  })
				: [];
			setRows(filteredRows);
		},
		[data],
	);

	React.useEffect(() => {
		if (!loading && !isRefetching) {
			setRows(data);
			requestSearch(searchText);
		}
	}, [loading, isRefetching, searchText, requestSearch, data]);

	const sortModel: GridSortModel = [];

	if (defaultSortItem) {
		sortModel.push(defaultSortItem);
	}

	return (
		<Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
			<TableHeader
				{...{
					title,
					value: searchText,
					onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
						requestSearch(event.target.value),
					clearSearch: () => requestSearch(''),
					buttonText,
					buttonLink,
					buttonIcon,
					onButtonClick,
					buttonColor,
					hideButton,
					hideSearch,
				}}
			/>
			{paginationMode === 'server' ? (
				<DataGrid
					rowSelection={false}
					disableColumnFilter
					autoHeight={autoHeight}
					getRowId={(row: any) => row[rowId]}
					onCellEditStop={onCellEditCommit}
					onCellModesModelChange={onEditRowsModelChange}
					classes={{ root: classes.root }}
					loading={loading}
					rows={rows ? rows : data && Array.isArray(data) ? data : []}
					columns={columns}
					checkboxSelection={checkboxSelection}
					pageSizeOptions={[5, 10, 25, 50, 100]}
					paginationMode={paginationMode}
					paginationModel={paginationModel}
					rowCount={rowCount}
					onPaginationModelChange={onPaginationModelChange}
					sx={{
						'.MuiTablePagination-displayedRows': {
							display: 'none', // 👈 to hide huge pagination number
						},
					}}
					slots={{
						pagination: (props) => (
							<CustomPagination hasNextPage={hasNextPage} {...props} />
						),
					}}
					initialState={{
						sorting: {
							sortModel,
						},
						pagination: { paginationModel: { pageSize } },
					}}
				/>
			) : (
				<DataGrid
					rowSelection={false}
					disableRowSelectionOnClick
					disableColumnFilter
					autoHeight={autoHeight}
					getRowId={(row: any) => row[rowId]}
					onCellEditStop={onCellEditCommit}
					onCellModesModelChange={onEditRowsModelChange}
					classes={{ root: classes.root }}
					loading={loading}
					rows={rows ? rows : data && Array.isArray(data) ? data : []}
					columns={columns}
					checkboxSelection={checkboxSelection}
					pageSizeOptions={[5, 10, 25, 50, 100]}
					rowCount={rowCount}
					initialState={{
						sorting: {
							sortModel,
						},
						pagination: { paginationModel: { pageSize } },
					}}
				/>
			)}
		</Box>
	);
};

export default GridWithToolbar;
